import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../Global/Layout';
import DataTable from '../../Global/DataTable';
import ExportExcel from '../../Global/ExportToExcel';
import { showError, showSuccess, showConfirm } from '../../../utils/sweetAlertConfig';
import { LottoBallsDisplay, LottoBallsWinning } from '../../../utils/LottoBalls';
import { API_CONFIG, ICON_ACTION, LOTTO_TYPE, PAYMENT_TYPE, PAYMENT_STATUS, generatePromptPayQRCodeURL, dateStampToDateDB, PAYMENT_TIME_MINUTES, parseBangkokInstant } from '../../../contexts/constants';
import { formatTHB } from '../../../utils/numberFormat';
import { getDiscountGroupText } from '../../../utils/discountPromotionLabel';
import DatePicker from 'react-datepicker';
import { th } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import Select from 'react-select';
import { formatDateTable, formatDateTimeTable } from '../../../utils/dateFormatter';
import useBodyScrollLock from '../../../hooks/useBodyScrollLock';

const ReportCreditMemberBet = ({ onLogout, userRole, creditRole, exportExcel, menuType }) => {
	const location = useLocation();
	const filter_period = useMemo(() => {
		const raw = new URLSearchParams(location.search).get('pr');
		return raw ? String(raw) : null;
	}, [location.search]);
	const filter_no_row_error = useMemo(() => {
		const raw = new URLSearchParams(location.search).get('nre');
		return raw ? String(raw) : null;
	}, [location.search]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [columnsExcelData, setColumnsExcelData] = useState([]);
  const [showSearchForm, setShowSearchForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [selectedOrderData, setSelectedOrderData] = useState(null);
  const [slipFile, setSlipFile] = useState(null);
  const [slipPreview, setSlipPreview] = useState(null);
  const [showViewSlipModal, setShowViewSlipModal] = useState(false);
  const [viewSlipUrl, setViewSlipUrl] = useState(null);
  const [showPrizeSlipModal, setShowPrizeSlipModal] = useState(false);
  const [prizeSlipUrl, setPrizeSlipUrl] = useState(null);
  const [bankInfo, setBankInfo] = useState(null);
  const [paymentChannel, setPaymentChannel] = useState('promptpay'); // 'promptpay' or 'bank'
  const [historySummary, setHistorySummary] = useState({
	pb: {
		id: 0,
		date: '',
	},
	mg: {
		id: 0,
		date: '',
	},
	total: 0,
	total_with_approve: 0,
	total_order_ball: 0,
	sum_price: 0
  });
  const getInitialFilters = () => {
    return {
      member_name: '',
      type: {
        powerball: true,
        megamillions: true,
      },
      period: [],
      agentName: '',
      prizeStatus: { won: false, lost: false },
      paymentType: {
        transfer: false,
        credit: false,
      },
      paymentStatus: {
		awaiting_payment: filter_no_row_error === '1' ? true : false,
        pending: filter_no_row_error === '1' ? true : false,
        approved: filter_no_row_error === '1' ? true : false,
        rejected: false,
		canceled: false,
      },
    };
  };
  const [filters, setFilters] = useState(getInitialFilters);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [periodOptions, setPeriodOptions] = useState([{ value: '', label: 'ทั้งหมด' }]);
  // fetchTrigger is the ONLY way to trigger a list fetch explicitly (avoids filter-state-triggered double fetches)
  const [fetchTrigger, setFetchTrigger] = useState(0);

  // Ref guard: list fetch must not run until after fetchDataSummary completes.
  // Using a ref (not state) so toggling it never causes a re-render or effect re-run on its own.
  const isInitializedRef = useRef(false);

  // Refs so fetchDataList always reads latest filter/date values without depending on them as useCallback deps.
  // This prevents setFilters (called inside fetchDataSummary) from causing fetchDataList to re-run.
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const startDateRef = useRef(startDate);
  startDateRef.current = startDate;
  const endDateRef = useRef(endDate);
  endDateRef.current = endDate;

  // Lock body scroll when modal is open
  useBodyScrollLock(showUploadModal || showViewSlipModal);

  // const formatTHB = (amount) => new Intl.NumberFormat('th-TH', { maximumFractionDigits: 0 }).format(amount);

	const fetchDataList = useCallback(async () => {
    // Guard: skip until fetchDataSummary has completed (handles StrictMode double-invoke too)
    if (!isInitializedRef.current) return;
    // Read latest filter/date values from refs (not from closure) so this function
    // is NOT recreated when filters/dates change — preventing unintended re-fetches.
    const filters = filtersRef.current;
    const startDate = startDateRef.current;
    const endDate = endDateRef.current;
    try {
      setLoading(true);

      // Build query parameters
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('pageSize', pageSize);

      // Add filters
      if (filters.member_name) params.append('member_name', filters.member_name);
      if (filters.period && filters.period.length > 0) {
        filters.period.forEach(periodId => params.append('period', periodId));
      }
      if (filters.agentName) params.append('agentName', filters.agentName);

	  	if(filter_period){
			params.append('period', filter_period);
		}

      // Add lottery type filters
      if (filters.type.powerball) params.append('type_powerball', 'true');
      if (filters.type.megamillions) params.append('type_megamillions', 'true');

      // Add prize status filters
      if (filters.prizeStatus.won) params.append('prize_won', 'true');
      if (filters.prizeStatus.lost) params.append('prize_lost', 'true');

      // Add payment type filters
      if (filters.paymentType.transfer) params.append('payment_transfer', 'true');
      if (filters.paymentType.credit) params.append('payment_credit', 'true');

      // Add payment status filters
	  if (filters.paymentStatus.awaiting_payment) params.append('payment_awaiting_payment', 'true');
      if (filters.paymentStatus.approved) params.append('payment_approved', 'true');
      if (filters.paymentStatus.pending) params.append('payment_pending', 'true');
      if (filters.paymentStatus.rejected) params.append('payment_rejected', 'true');
      if (filters.paymentStatus.canceled) params.append('payment_canceled', 'true');

      // Add date range
      if (startDate) params.append('startDate', dateStampToDateDB(startDate));
      if (endDate) params.append('endDate', dateStampToDateDB(endDate));

      const response = await axios.get(`${API_CONFIG.BASE_URL}/report/order-history?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

	      if (response.data.success && Array.isArray(response.data.data)) {
			response.data.data.forEach((v, i) => {
				response.data.data[i].group_text = getDiscountGroupText(v);
			});
        setData(response.data.data);
        setTotalItems(response.data.total || 0);
        setTotalPages(response.data.totalPages || 1);

        const formattedData = response.data.data.map(r => ({
          drawDate: r.drawDate ?? formatDateTable(r.drawDate) ?? '',
          member_name: (r.member_name ?? r.member_name ?? '').toString().replace(/"/g, ''),
          type: r.type ?? r.lotteryType ?? '',
          betNumber: r.grayBalls ?? r.grayBalls+' + '+ r.redBall ?? '',
          agentName: (r.agentName ?? r.agentName ?? '').toString().replace(/"/g, ''),
          credit: r.credit ?? r.credit ?? '',
        }));
        setColumnsExcelData(formattedData);
      } else {
        console.error('Invalid results data format');
        setData([]);
      }
    } catch (error) {
      console.error('Error fetching results:', error);
      showError('เกิดข้อผิดพลาด', error.message || 'ไม่สามารถโหลดข้อมูลได้');
      setData([]);
    }finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, filter_period, fetchTrigger]); // NO filters/startDate/endDate — read from refs above

  const fetchDataSummary = useCallback(async () => {
		if(userRole !== 'customer'){
			try {
				const params = new URLSearchParams();
				if(filter_period){
					params.append('period', filter_period);
				}
				const response = await axios.get(`${API_CONFIG.BASE_URL}/report/order-summary?${params.toString()}`, {
					headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
				});

				setHistorySummary({
					pb: {
						id: response.data.pb.award_results_id,
						date: response.data.pb.date,
					},
					mg: {
						id: response.data.mg.award_results_id,
						date: response.data.mg.date,
					},
					total: response.data.total,
					total_with_approve: response.data.total_with_approve,
					total_order_ball: response.data.total_order_ball,
					sum_price: response.data.sum_price
				});
				if(response.data.pb.award_results_id || response.data.mg.award_results_id){
					setFilters(prevFilters => ({
						...prevFilters,
						period: [response.data.pb.award_results_id.toString(), response.data.mg.award_results_id.toString()]
					}));
				}
			} catch (error) {
				console.error('Error fetching results:', error);
			}
		}
  	}, [userRole, filter_period]);

  const fetchBankInfo = useCallback(async () => {
    try {
      const response = await axios.get(`${API_CONFIG.BASE_URL}/config/sale`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.data.success) {
        setBankInfo(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching bank info:', error);
    }
  }, []);

  const fetchPeriodOptions = useCallback(async () => {
    try {
      const response = await axios.get(`${API_CONFIG.BASE_URL}/report/award-results`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.data.success && Array.isArray(response.data.data)) {
        const options = [
          { value: '', label: 'ทั้งหมด' },
          ...response.data.data.map(item => ({
            value: item.id.toString(),
            label: item.label + ' (' + (item.lot_type === 1 ? LOTTO_TYPE.powerball : LOTTO_TYPE.megamillions) + ')'
          }))
        ];
        setPeriodOptions(options);
      }
    } catch (error) {
      console.error('Error fetching period options:', error);
    }
  }, []);

  // Initialize once on mount: fetch bank info + period options in parallel, await summary (which calls
  // setFilters internally), then trigger the first list fetch via setFetchTrigger.
  // StrictMode double-invoke protection:
  //   - `isInitializedRef.current` is reset to false on each mount/remount (line below)
  //   - `cancelled` flag ensures only the LAST IIFE (the real mount) can trigger the fetch
  //   - The list effect guard (isInitializedRef.current) blocks any stray calls before init is done
  useEffect(() => {
    let cancelled = false;
    isInitializedRef.current = false; // reset on every (re)mount so the guard works correctly
    (async () => {
      fetchBankInfo();
      fetchPeriodOptions();
      await fetchDataSummary();
      if (!cancelled) {
        isInitializedRef.current = true;  // unlock the list-fetch guard
        setFetchTrigger(v => v + 1);      // trigger exactly one fetch
      }
    })();
    return () => {
      cancelled = true;
      isInitializedRef.current = false; // reset on cleanup so StrictMode remount starts fresh
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch list whenever fetchDataList reference changes.
  // fetchDataList only changes when currentPage, pageSize, filter_period, or fetchTrigger change.
  // setFilters alone does NOT change fetchDataList (filters are read from ref), so no spurious re-fetches.
  useEffect(() => {
    fetchDataList();
  }, [fetchDataList]);

  if (!['sysadmin', 'owner', 'admin', 'agent', 'customer'].includes(userRole)) {
		window.location.href = '/forbidden';
		return null;
	}

  const headersExcel = [
    { key: 'drawDate', label: 'วันที่' },
    { key: 'member_name', label: 'ชื่อลูกค้า' },
    { key: 'type', label: 'ประเภท' },
    { key: 'betNumber', label: 'ลูกบอลเทา' },
    { key: 'agentName', label: 'ชื่อตัวแทน' },
  ];

  const baseColumns = [
    {
      key: 'period',
      header: 'งวดวันที่',
      className: 'min-w-40 text-center',
      render: (row) => (
		<div>
        	<span>{row.period || row.drawPeriod || '-'}</span>
			{userRole === 'customer' ? <div className="text-xs text-gray-500">ซื้อเมื่อ: {formatDateTimeTable(row.create_date)}</div> : null}
		</div>
      )
    },
    {
      key: 'memberName',
      header: 'ชื่อลูกค้า',
      className: 'min-w-60',
      render: (row) => (
		<>
			<div className="text-left">{row.member_name}</div>
			<small className="text-left text-gray-500">ซื้อเมื่อ: {formatDateTimeTable(row.create_date)}</small>
		</>
      ),
    },
    {
      key: 'lotteryType',
      header: 'ประเภท',
      render: (row) => (
        <div className="text-center">
          <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold min-w-32 ${
          row.type === 'powerball'
            ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
            : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
          }`}>
          {LOTTO_TYPE[row.type]}
          </span>
        </div>
      )
    },
    {
		key: 'betNumber',
		header: 'เลขที่สั่งซื้อ',
		render: (row) => {
			if(row.prize === 1){
				return <LottoBallsWinning grayBalls={row.grayBalls} specialBall={row.redBall} winningGrayBalls={row.winGrayBalls} winningSpecialBall={row.winRedBall} type={row.type} paymentStatus={row.paymentStatus} />;
			}else{
				return <LottoBallsDisplay grayBalls={row.grayBalls} specialBall={row.redBall} type={row.type} paymentStatus={row.paymentStatus} />;
			}
       	}
    },
    {
      key: 'quantity',
      header: 'สั่งซื้อ (บาท)',
      render: (row) => (
        <div className="text-center">
          {row.isPromotionTicket || row.quantityDisplay === 'Pro'
            ? <span className="font-semibold text-green-600">Pro</span>
            : formatTHB(row.quantity)}
        </div>
      )
    },
    {
      key: 'payment',
      header: 'การชำระเงิน',
      render: (row) => (
        <div className="text-center">
          <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold min-w-32 ${
          row.paymentType === 'transfer'
            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
            : 'bg-pink-100 text-pink-700 dark:bg-pink-900 dark:text-pink-300'
          }`}>
          {PAYMENT_TYPE[row.paymentType]}
          </span>
          {row.paymentType === 'transfer' && (
            <div className="mt-3">
              <span className="text-sm text-gray-500">สถานะ:</span> {row.paymentStatus === 'approved' ? (
                <>
                  <span className="text-green-600 font-semibold">{PAYMENT_STATUS[row.paymentStatus]}</span>
                </>
              ) : row.paymentStatus === 'awaiting_payment' ? (
                <>
                  <span className="text-gray-600 font-semibold">{PAYMENT_STATUS[row.paymentStatus]}</span>
                </>
              ) : row.paymentStatus === 'pending' ? (
                <>
                  <span className="text-yellow-600 font-semibold">{PAYMENT_STATUS[row.paymentStatus]}</span>
                </>
              ) :
              (
                <>
                  <span className="text-red-600 font-semibold">{PAYMENT_STATUS[row.paymentStatus]}</span><br/>
                  <span className="text-sm">{row.paymentNote}</span>
                </>
              )}
            </div>
          )}
        </div>
      )
    },
    {
      key: 'agentName',
      header: 'ชื่อตัวแทน',
      render: (row) => (
        <div className={`text-left`}>{row.agentName}</div>
      )
    },
  ];

  if(userRole === 'customer'){
	baseColumns.splice(1, 1);
  }

  if(userRole === 'agent'){
	baseColumns.splice(6, 1);
  }

  let columns = [...baseColumns];

  if (userRole === 'customer') {
    // Insert the 'prize' column for customers
    const prizeColumn = {
		key: 'prize',
		header: 'รางวัล',
		render: (row) => {
			if(row.prize === 1){
				return <div className="text-center">
					<span className="text-green-600 font-semibold text-xs">ถูกรางวัล</span>
					<br/>
					<span className="text-green-600 font-semibold text-xl">{formatTHB(row.prizeAmount)} บาท</span>
					{row.prizeFileId && (
						<div className="mt-2">
							<button
								onClick={() => handleViewPrizeSlipClick(row.prizeFileId)}
								className="px-3 py-1 bg-green-500 hover:bg-green-600 text-white text-xs rounded-md transition-colors flex items-center gap-1 mx-auto"
							>
								<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
								</svg>
								ดูสลิปจ่ายรางวัล
							</button>
						</div>
					)}
					</div>;
			}else if(row.prize === 2){
				return <div className="text-center"><span className="text-red-300 font-semibold text-xs">ไม่ถูกรางวัล</span></div>;
			}else{
				return '';
			}
		}
    };
    columns.splice(5, 0, prizeColumn); // Insert at index 5 (after quantity)
  }

  const gotoPage = (p) => {
    if (p < 1 || p > totalPages) return;
    setCurrentPage(p);
  };

	const handleSearch = () => {
		// React 18 batches both state updates → single render → fetchDataList ref changes once → one fetch
		setCurrentPage(1);
		setFetchTrigger(v => v + 1);
	};

	const handleClearSearch = () => {
		const initialFilters = getInitialFilters();
		if (historySummary.pb.id || historySummary.mg.id) {
			initialFilters.period = [historySummary.pb.id.toString(), historySummary.mg.id.toString()];
		}
		// React 18 batches all these → single render → one fetch
		setFilters(initialFilters);
		setStartDate(null);
		setEndDate(null);
		setCurrentPage(1);
		setFetchTrigger(v => v + 1);
	};

  const handleUploadSlipClick = (orderId) => {
    setSelectedOrderId(orderId);
    // หาข้อมูล order จาก data
    const orderData = data.find(item => item.billId === orderId);
    setSelectedOrderData(orderData);
    setSlipFile(null);
    setSlipPreview(null);
    setShowUploadModal(true);
  };

  const handleViewSlipClick = async (billId) => {
    try {
      const response = await axios.get(`${API_CONFIG.BASE_URL}/orders/slip/${billId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.data.success && response.data.slipUrl) {
        setViewSlipUrl(response.data.slipUrl);
        setShowViewSlipModal(true);
      } else {
        showError('ไม่พบสลิป', 'ไม่พบไฟล์สลิปการโอนเงิน');
      }
    } catch (error) {
      console.error('Error fetching slip:', error);
      showError('เกิดข้อผิดพลาด', 'ไม่สามารถโหลดสลิปการโอนเงินได้');
    }
  };

  const handleViewPrizeSlipClick = async (fileId) => {
    if (!fileId) {
      showError('ไม่พบสลิป', 'ไม่พบไฟล์สลิปการจ่ายเงินรางวัล');
      return;
    }

    try {
      const response = await axios.get(`${API_CONFIG.BASE_URL}/report/prize-slip/${fileId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.data.success && response.data.slipUrl) {
        setPrizeSlipUrl(response.data.slipUrl);
        setShowPrizeSlipModal(true);
      } else {
        showError('ไม่พบสลิป', 'ไม่พบไฟล์สลิปการจ่ายเงินรางวัล');
      }
    } catch (error) {
      console.error('Error fetching prize slip:', error);
      showError('เกิดข้อผิดพลาด', 'ไม่สามารถโหลดสลิปการจ่ายเงินรางวัลได้');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        showError('ไฟล์ใหญ่เกินไป', 'กรุณาเลือกไฟล์ที่มีขนาดไม่เกิน 5MB');
        return;
      }
      setSlipFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setSlipPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveSlip = () => {
    setSlipFile(null);
    setSlipPreview(null);
  };

  const handleUploadSlip = async () => {
    if (!slipFile) {
      showError('กรุณาเลือกไฟล์', 'กรุณาเลือกไฟล์สลิปก่อนทำการอัพโหลด');
      return;
    }

    const result = await showConfirm({
      title: 'ยืนยันการอัพโหลดสลิป',
      html: 'คุณต้องการอัพโหลดสลิปการชำระเงินใช่หรือไม่?'
    });

    if (result.isConfirmed) {
      try {
        const formData = new FormData();
        formData.append('orderId', selectedOrderId);
        formData.append('paymentType', paymentChannel);
        formData.append('slip', slipFile);

        const response = await fetch(`${API_CONFIG.BASE_URL}/orders/upload-slip`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: formData
        });

        const resultData = await response.json();

        if (response.ok && resultData.success) {
          setShowUploadModal(false);
          setSlipFile(null);
          setSlipPreview(null);
          setSelectedOrderId(null);
          setSelectedOrderData(null);
          showSuccess('อัพโหลดสลิปสำเร็จ', 'คำสั่งซื้อของคุณอยู่ระหว่างรอการตรวจสอบ');
          setFetchTrigger(v => v + 1); // Refresh data
        } else {
          showError('เกิดข้อผิดพลาด', resultData.message || 'ไม่สามารถอัพโหลดสลิปได้');
        }
      } catch (error) {
        console.error('Error uploading slip:', error);
        showError('เกิดข้อผิดพลาด', 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้');
      }
    }
  };

  const agentNameOptions = [
    { value: '', label: 'ทั้งหมด' },
    ...[...new Set(data.map(item => item.agentName))]
      .filter(Boolean) // Filter out null/undefined agent names
      .map(name => ({ value: name, label: name }))
  ];

  const toggleSearchForm = () => {
		setShowSearchForm(!showSearchForm);
	};

  const handleCloseUploadModal = () => {
    setShowUploadModal(false);
    setSlipFile(null);
    setSlipPreview(null);
    setSelectedOrderId(null);
    setSelectedOrderData(null);
  };

  const handleCloseAllModals = () => {
    handleCloseUploadModal();
    setFetchTrigger(v => v + 1);
  };

  return (
    <Layout title={`${creditRole === 'owner' ? 'รายงานการสั่งซื้อ' : 'ประวัติรายการสั่งซื้อของลูกค้า'}`} onLogout={onLogout} userRole={userRole}>
		<div className="flex justify-end gap-3 mb-4">
			{/* Toggle Search */}
			<button
			onClick={toggleSearchForm}
			className={`flex items-center gap-2 px-4 py-2 text-white rounded-lg transition shadow-lg ${
				showSearchForm
				? 'bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700'
				: 'bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700'
			}`}
			>
			{showSearchForm ? ICON_ACTION.CLOSE : ICON_ACTION.SEARCH}
			<span className='hidden md:inline'>ค้นหา</span>
			</button>
			{/* Export CSV */}
			{ exportExcel === 'true' && creditRole === 'owner' && (
			<ExportExcel
				data={columnsExcelData}
				headers={headersExcel}
				fileName={`ReportWining-${dateStampToDateDB(new Date())}.xlsx`}
			/>
			)}
		</div>

		{showSearchForm && (
			<div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow mb-4">
				<div className={`grid grid-cols-1 md:grid-cols-2 ${creditRole === 'owner' ? 'lg:grid-cols-2' : 'lg:grid-cols-2'} gap-4`}>
					{/* งวดวันที่ */}
					{!filter_period ? <div>
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
							งวดวันที่
						</label>
						<Select
							options={periodOptions.filter(opt => opt.value !== '')}
							value={periodOptions.filter(opt => filters.period.includes(opt.value))}
							onChange={(selectedOptions) => setFilters(prev => ({ ...prev, period: selectedOptions ? selectedOptions.map(opt => opt.value) : [] }))}
							className="w-full"
							placeholder="เลือกงวด (เลือกได้หลายงวด)"
							isMulti
							isClearable
						/>
					</div> : null}

					{/* ประเภท */}
					{!filter_period ? <div>
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
							ประเภท
						</label>
						<div className="flex items-center gap-6 mt-2">
							<label htmlFor="type_powerball_checkbox" className="flex items-center cursor-pointer">
								<input
									type="checkbox"
									id="type_powerball_checkbox"
									checked={filters.type.powerball}
									onChange={(e) => setFilters(prev => ({ ...prev, type: { ...prev.type, powerball: e.target.checked } }))}
									className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
								/>
								<span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{LOTTO_TYPE.powerball}</span>
							</label>
							<label htmlFor="type_megamillions_checkbox" className="flex items-center cursor-pointer">
								<input
									type="checkbox"
									id="type_megamillions_checkbox"
									checked={filters.type.megamillions}
									onChange={(e) => setFilters(prev => ({ ...prev, type: { ...prev.type, megamillions: e.target.checked } }))}
									className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
								/>
								<span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{LOTTO_TYPE.megamillions}</span>
							</label>
						</div>
					</div> : null}

					{/* ตั้งแต่วันที่ */}
					<div>
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
							ตั้งแต่วันที่
						</label>
						<DatePicker
							selected={startDate}
							onChange={(date) => setStartDate(date)}
							selectsStart
							startDate={startDate}
							endDate={endDate}
							locale={th}
							dateFormat="dd/MM/yyyy"
							placeholderText="เลือกวันที่เริ่มต้น"
							className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
							isClearable
						/>
					</div>

					{/* ถึงวันที่ */}
					<div>
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
							ถึงวันที่
						</label>
						<DatePicker
							selected={endDate}
							onChange={(date) => setEndDate(date)}
							selectsEnd
							startDate={startDate}
							endDate={endDate}
							minDate={startDate}
							locale={th}
							dateFormat="dd/MM/yyyy"
							placeholderText="เลือกวันที่สิ้นสุด"
							className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
							isClearable
						/>
					</div>

					{/* ค้นหาตามชื่อ */}
					{userRole !== 'customer' && (
						<div>
							<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
								ชื่อลูกค้า
							</label>
							<input
								type="text"
								value={filters.member_name}
								onChange={(e) => setFilters(prev => ({ ...prev, member_name: e.target.value }))}
								className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
								placeholder="ค้นหาตามชื่อ"
							/>
						</div>
					)}

					{/* ชื่อตัวแทน */}
					{(userRole === 'sysadmin' || userRole === 'owner' || userRole === 'admin') && (
						<div>
							<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
								ชื่อตัวแทน
							</label>
							<Select
								options={agentNameOptions}
								value={agentNameOptions.find(option => option.value === filters.agentName)}
								onChange={(selectedOption) => setFilters(prev => ({ ...prev, agentName: selectedOption ? selectedOption.value : '' }))}
								className="w-full"
								placeholder="เลือกตัวแทน"
								isClearable
							/>
						</div>
					)}

					{/* ผลรางวัล */}
					<div>
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
							ผลรางวัล
						</label>
						<div className="flex items-center gap-6 mt-2">
							<label htmlFor="won_checkbox" className="flex items-center cursor-pointer">
								<input
									type="checkbox"
									id="won_checkbox"
									checked={filters.prizeStatus.won}
									onChange={(e) => setFilters(prev => ({ ...prev, prizeStatus: { ...prev.prizeStatus, won: e.target.checked } }))}
									className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
								/>
								<span className="ml-2 text-sm text-gray-700 dark:text-gray-300">ถูกรางวัล</span>
							</label>
							<label htmlFor="lost_checkbox" className="flex items-center cursor-pointer">
								<input
									type="checkbox"
									id="lost_checkbox"
									checked={filters.prizeStatus.lost}
									onChange={(e) => setFilters(prev => ({ ...prev, prizeStatus: { ...prev.prizeStatus, lost: e.target.checked } }))}
									className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
								/>
								<span className="ml-2 text-sm text-gray-700 dark:text-gray-300">ไม่ถูกรางวัล</span>
							</label>
						</div>
					</div>

					{/* ประเภทการชำระเงิน */}
					<div>
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
							ประเภทการชำระเงิน
						</label>
						<div className="flex items-center gap-6 mt-2">
							<label htmlFor="payment_transfer_checkbox" className="flex items-center cursor-pointer">
								<input
									type="checkbox"
									id="payment_transfer_checkbox"
									checked={filters.paymentType.transfer}
									onChange={(e) => setFilters(prev => ({ ...prev, paymentType: { ...prev.paymentType, transfer: e.target.checked } }))}
									className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
								/>
								<span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{PAYMENT_TYPE.transfer}</span>
							</label>
							<label htmlFor="payment_credit_checkbox" className="flex items-center cursor-pointer">
								<input
									type="checkbox"
									id="payment_credit_checkbox"
									checked={filters.paymentType.credit}
									onChange={(e) => setFilters(prev => ({ ...prev, paymentType: { ...prev.paymentType, credit: e.target.checked } }))}
									className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
								/>
								<span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{PAYMENT_TYPE.discount}</span>
							</label>
						</div>
					</div>

					{/* สถานะการทำรายการ */}
					<div>
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
							สถานะการทำรายการ (สำหรับโอนเงิน)
						</label>
						<div className="flex items-center gap-6 mt-2 flex-wrap">
							{Object.keys(filters.paymentStatus).map(statusKey => (
								<label key={statusKey} htmlFor={`status_${statusKey}_checkbox`} className="flex items-center cursor-pointer">
									<input
										type="checkbox"
										id={`status_${statusKey}_checkbox`}
										checked={filters.paymentStatus[statusKey]}
										onChange={(e) => setFilters(prev => ({ ...prev, paymentStatus: { ...prev.paymentStatus, [statusKey]: e.target.checked } }))}
										className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
									/>
									<span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{PAYMENT_STATUS[statusKey]}</span>
								</label>
							))}
						</div>
					</div>
				</div>
				<div className="mt-4 flex justify-end gap-2">
					<button
						onClick={handleSearch}
						className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md font-medium transition-colors flex items-center gap-2"
					>
						{ICON_ACTION.SEARCH}
						ค้นหา
					</button>
					<button
						onClick={handleClearSearch}
						className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-md font-medium transition-colors flex items-center gap-2"
					>
						{ICON_ACTION.CLEAR}
						ล้าง
					</button>
				</div>
			</div>
		)}

		{/* Header */}
		{userRole !== 'customer' ?
			<div className="flex items-center justify-between mb-4 ">
				<div>
					<p className={`text-sm mt-1 ${filter_period ? 'text-red-500 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
						{
							historySummary.pb.id && historySummary.mg.id ? (
								<>
									รายการคำสั่งซื้อ {LOTTO_TYPE.powerball} ประจำงวดวันที่ {historySummary.pb.date || 'ทั้งหมด'} และ {LOTTO_TYPE.megamillions} ประจำงวดวันที่ {historySummary.mg.date || 'ทั้งหมด'} รวมจำนวน <span className="font-semibold text-gray-900 dark:text-white">{formatTHB(historySummary.total || 0)}</span> รายการ
								</>
							) : (
								historySummary.mg.id ? (
									<>
										รายการคำสั่งซื้อ {LOTTO_TYPE.megamillions} ประจำงวดวันที่ {historySummary.mg.date || 'ทั้งหมด'} จำนวน <span className="font-semibold text-gray-900 dark:text-white">{formatTHB(historySummary.total || 0)}</span> รายการ
									</>
								) : (
									<>
										รายการคำสั่งซื้อ {LOTTO_TYPE.powerball} ประจำงวดวันที่ {historySummary.pb.date || 'ทั้งหมด'} จำนวน <span className="font-semibold text-gray-900 dark:text-white">{formatTHB(historySummary.total || 0)}</span> รายการ
									</>
								)
							)
						}
					</p>
				</div>
			</div>
		 : null}

		{/* Stats */}
		{userRole !== 'customer' ?
		<div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
			<div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border border-blue-200 dark:border-blue-700 rounded-xl p-4">
				<div className="flex items-center gap-3">
					<div className="p-3 bg-blue-500 rounded-lg">
						<svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
						</svg>
					</div>
					<div>
						<p className="text-sm text-gray-600 dark:text-gray-400">รอตรวจสอบ</p>
						<p className="text-2xl font-bold text-gray-900 dark:text-white">{formatTHB(historySummary.total_with_approve || 0)}</p>
					</div>
				</div>
			</div>

			<div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border border-green-200 dark:border-green-700 rounded-xl p-4">
				<div className="flex items-center gap-3">
					<div className="p-3 bg-green-500 rounded-lg">
						<svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
						</svg>
					</div>
					<div>
						<p className="text-sm text-gray-600 dark:text-gray-400">จำนวนใบรวม</p>
						<p className="text-2xl font-bold text-gray-900 dark:text-white">{formatTHB(historySummary.total_order_ball || 0)}</p>
					</div>
				</div>
			</div>

			<div className="bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-900/20 dark:to-yellow-800/20 border border-yellow-200 dark:border-yellow-700 rounded-xl p-4">
				<div className="flex items-center gap-3">
					<div className="p-3 bg-yellow-500 rounded-lg">
						<svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
						</svg>
					</div>
					<div>
						<p className="text-sm text-gray-600 dark:text-gray-400">ยอดเงินรวม</p>
						<div className='flex items-center gap-2'>
							<p className="text-2xl font-bold text-gray-900 dark:text-white">{formatTHB(historySummary.sum_price || 0)}</p>
							<p className="text-sm text-gray-600 dark:text-gray-400">(รายการที่ถูกอนุมัติ และหักส่วนลดแล้ว)</p>
						</div>
					</div>
				</div>
			</div>
		</div> : null}

		<DataTable
			columns={columns}
			data={data}
			loading={loading}
			groupBy="billCode"
			page={currentPage}
			pageSize={pageSize}
			totalItems={totalItems}
			onPageChange={gotoPage}
			onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
			emptyMessage="ไม่มีข้อมูลผลรางวัล"
			showPagination={true}
			paginationUnitLabel="บิล"
			groupHeaderAction={(row) => {
				if (row.paymentType === 'transfer') {
					if (row.paymentStatus === 'awaiting_payment') {
						return menuType !== "report" ? (
							(() => {
									const created = parseBangkokInstant(row.create_date);
								const now = new Date();
								const diffMinutes = (now - created) / (1000 * 60);
								return diffMinutes <= PAYMENT_TIME_MINUTES;
							})() ? (
								<button
									onClick={() => handleUploadSlipClick(row.billId)}
									className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white text-xs rounded-md transition-colors flex items-center gap-1"
								>
									<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
									</svg>
									อัพโหลดสลิป
								</button>
							) : null
						) : null;
					} else if (row.paymentStatus === 'pending' || row.paymentStatus === 'approved') {
						return (
							<button
								onClick={() => handleViewSlipClick(row.billId)}
								className="px-3 py-1 bg-gray-400 hover:bg-gray-500 text-white text-xs rounded-md transition-colors flex items-center gap-1"
							>
								<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
								</svg>
								ดูสลิป
							</button>
						);
					}
				}
				return null;
			}}
		/>

	{showUploadModal && (
      <UploadSlipModal
        selectedOrderData={selectedOrderData}
        bankInfo={bankInfo}
        paymentChannel={paymentChannel}
        setPaymentChannel={setPaymentChannel}
        slipFile={slipFile}
        slipPreview={slipPreview}
        handleFileChange={handleFileChange}
        handleRemoveSlip={handleRemoveSlip}
        handleUploadSlip={handleUploadSlip}
        onClose={handleCloseUploadModal}
        onCloseAll={handleCloseAllModals}
      />
    )}
	{showViewSlipModal && <ViewSlipModal />}
	{showPrizeSlipModal && <PrizeSlipModal />}
    </Layout>
  );

  function ViewSlipModal() {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
        <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-3xl my-auto">
          {/* Header */}
          <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              หลักฐานการโอนเงิน
            </h3>
            <button
              onClick={() => {
                setShowViewSlipModal(false);
                setViewSlipUrl(null);
              }}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              aria-label="ปิด"
            >
              <svg className="w-6 h-6 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="p-6">
            <div className="flex justify-center">
              <img
                src={viewSlipUrl || '/slip-bank-empty.jpg'}
                alt="Payment Slip"
                style={{ height: '400px' }}
                className="object-contain rounded-lg border border-gray-300 dark:border-gray-600"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end">
            <button
              onClick={() => {
                setShowViewSlipModal(false);
                setViewSlipUrl(null);
              }}
              className="px-6 py-3 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-white rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition font-semibold"
            >
              ปิด
            </button>
          </div>
        </div>
      </div>
    );
  }

  function PrizeSlipModal() {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
        <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-3xl my-auto">
          {/* Header */}
          <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              สลิปการจ่ายเงินรางวัล
            </h3>
            <button
              onClick={() => {
                setShowPrizeSlipModal(false);
                setPrizeSlipUrl(null);
              }}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              aria-label="ปิด"
            >
              <svg className="w-6 h-6 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="p-6">
            <div className="flex justify-center">
              <img
                src={prizeSlipUrl || '/slip-bank-empty.jpg'}
                alt="Prize Payment Slip"
                style={{ maxHeight: '500px' }}
                className="object-contain rounded-lg border border-gray-300 dark:border-gray-600"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '/slip-bank-empty.jpg';
                }}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end">
            <button
              onClick={() => {
                setShowPrizeSlipModal(false);
                setPrizeSlipUrl(null);
              }}
              className="px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition font-semibold"
            >
              ปิด
            </button>
          </div>
        </div>
      </div>
    );
  }

}

// UploadSlipModal Component with its own countdown logic
const UploadSlipModal = ({
  selectedOrderData,
  bankInfo,
  paymentChannel,
  setPaymentChannel,
  slipFile,
  slipPreview,
  handleFileChange,
  handleRemoveSlip,
  handleUploadSlip,
  onClose,
  onCloseAll
}) => {
  const [countdownMs, setCountdownMs] = useState(null);

  // Format remaining milliseconds into Thai minutes/seconds
  const formatTimeLeft = (ms) => {
    if (ms == null) return '';
    if (ms <= 0) return 'หมดเวลาแล้ว';
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes} นาที ${seconds} วินาที`;
  };

  useEffect(() => {
    let interval = null;
    if (selectedOrderData && selectedOrderData.create_date) {
	      const created = parseBangkokInstant(selectedOrderData.create_date);
      if (!isNaN(created)) {
        const deadline = new Date(created.getTime() + PAYMENT_TIME_MINUTES * 60 * 1000);
        const update = () => {
          const now = new Date();
          const diff = deadline - now;
          setCountdownMs(diff > 0 ? diff : 0);
        };
        update();
        interval = setInterval(update, 1000);
      }
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [selectedOrderData]);

  // When countdown reaches zero, close modal and show error
  useEffect(() => {
    if (countdownMs === 0) {
      if (typeof onCloseAll === 'function') {
        onCloseAll();
      } else if (typeof onClose === 'function') {
        onClose();
      }
      showError('หมดเวลาชำระเงิน', 'เวลาชำระเงินของคำสั่งซื้อนี้หมดแล้ว หากมีข้อสงสัยหรือต้องการความช่วยเหลือกรุณาติดต่อแอดมิน');
    }
  }, [countdownMs, onClose, onCloseAll]);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl my-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">
            อัพโหลดหลักฐานการชำระเงิน
          </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            aria-label="ปิด"
          >
            <svg className="w-6 h-6 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {/* Order Information */}
          {selectedOrderData && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-2 border-green-300 dark:border-green-700 rounded-xl p-4">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                รายละเอียดคำสั่งซื้อ
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">เลขที่คำสั่งซื้อ</p>
                  <p className="text-base font-bold text-gray-900 dark:text-white">{selectedOrderData.billCode || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">งวดวันที่</p>
                  <p className="text-base font-semibold text-gray-900 dark:text-white">{selectedOrderData.period || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">จำนวนใบ</p>
                  <p className="text-base font-semibold text-gray-900 dark:text-white">{selectedOrderData.count_bill || 0} ใบ</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">จำนวนเงินที่ต้องชำระ</p>
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">{formatTHB(selectedOrderData.price_bill_final || 0)} บาท</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">กรุณาชำระเงินภายใน {PAYMENT_TIME_MINUTES} นาที นับจากเวลาที่สั่งซื้อ</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">สั่งซื้อเมื่อ: {selectedOrderData.create_date ? formatDateTimeTable(selectedOrderData.create_date) : '-'}</p>
                  <p className="text-base font-semibold text-red-600 dark:text-red-400">{countdownMs == null ? 'กำลังโหลด...' : (countdownMs > 0 ? formatTimeLeft(countdownMs) : 'หมดเวลาแล้ว')}</p>
                </div>
              </div>
              {selectedOrderData.is_discount === 1 && (
                <div className="mt-3 pt-3 border-t border-green-200 dark:border-green-700">
                  <p className="text-xs text-green-700 dark:text-green-300">
                    <svg className="w-4 h-4 inline mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                    </svg>
                    {selectedOrderData.group_text || 'มีส่วนลด'}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Bank Information */}
          {bankInfo && selectedOrderData && (
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-5">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                ช่องทางการชำระเงิน
              </h4>

              {/* Payment Channel Selection */}
              <div className="mb-4">
                <div className="flex gap-4">
                  {bankInfo.prompt_pay && (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="paymentChannel"
                        value="promptpay"
                        checked={paymentChannel === 'promptpay'}
                        onChange={(e) => setPaymentChannel(e.target.value)}
                        className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">จ่ายผ่าน พร้อมเพย์</span>
                    </label>
                  )}
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="paymentChannel"
                      value="bank"
                      checked={paymentChannel === 'bank'}
                      onChange={(e) => setPaymentChannel(e.target.value)}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">จ่ายผ่าน บัญชีธนาคาร</span>
                  </label>
                </div>
              </div>

              {/* QR Code Section */}
              {paymentChannel === 'promptpay' && bankInfo.prompt_pay && (
                <div className="grid md:grid-cols-1 gap-4 mb-3">
                  <div className="flex flex-col items-center justify-center bg-white dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                    <div className="bg-white rounded-lg">
                      <img
                        src={generatePromptPayQRCodeURL(bankInfo.prompt_pay, selectedOrderData.price_bill_final || 0)}
                        alt="PromptPay QR Code"
                        className="w-40 h-40"
                        onError={(e) => {
                          e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'%3E%3Crect width='200' height='200' fill='%23f3f4f6'/%3E%3Ctext x='100' y='100' text-anchor='middle' fill='%236b7280' font-size='14'%3EQR Code%3C/text%3E%3C/svg%3E";
                        }}
                      />
                    </div>
                    {bankInfo.prompt_pay_name && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">ชื่อบัญชี: {bankInfo.prompt_pay_name}</p>
                    )}
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">เลขพร้อมเพย์: {bankInfo.prompt_pay}</p>
                  </div>
                </div>
              )}

              {/* Bank Account Details */}
              {paymentChannel === 'bank' && (
                <div className="grid md:grid-cols-1 gap-4 mb-3">
                  <div className="bg-white dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                    <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                      {bankInfo.bank_name && (
                        <p><span className="font-medium">ธนาคาร:</span> {bankInfo.bank_name}</p>
                      )}
                      {bankInfo.bank_account && (
                        <p><span className="font-medium">เลขบัญชี:</span> {bankInfo.bank_account}</p>
                      )}
                      {bankInfo.bank_account_name && (
                        <p><span className="font-medium">ชื่อบัญชี:</span> {bankInfo.bank_account_name}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="grid md:grid-cols-1 gap-4">
                <div className="bg-white dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                  <p className="text-base font-semibold text-gray-900 dark:text-white mb-2">ยอดที่ต้องชำระ</p>
                  <p className="text-lg font-bold text-green-600 dark:text-green-400 mt-2">{formatTHB(selectedOrderData.price_bill_final || 0)} บาท</p>
                </div>
              </div>

              {/* Warning Note */}
              <div className="mt-4 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700 rounded-lg p-3">
                <div className="flex gap-2">
                  <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div className="text-xs text-yellow-800 dark:text-yellow-200">
                    <p className="font-semibold">หมายเหตุ:</p>
                    <ul className="mt-1 space-y-1 list-disc list-inside">
                      <li>กรุณาโอนเงินตามยอดที่ระบุ ภายใน {PAYMENT_TIME_MINUTES} นาที หลังจากกด "ดำเนินการชำระเงิน"</li>
                      <li>หากเกิน {PAYMENT_TIME_MINUTES} นาที คำสั่งซื้อจะถูกยกเลิก</li>
                      <li>อัพโหลดหลักฐานการชำระเงินด้านล่าง</li>
                      <li>คำสั่งซื้อจะสมบูรณ์เมื่อผู้ดูแลอนุมัติ</li>
                      <li>หากมีข้อสงสัย กรุณาติดต่อผู้แอดมิน</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Upload Section */}
          <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl p-4">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              อัพโหลดหลักฐานการชำระเงิน
            </h4>
            {!slipPreview ? (
              <div>
                <label className="block w-full cursor-pointer">
                  <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center hover:border-blue-500 transition">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">คลิกเพื่อเลือกไฟล์</p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">รองรับไฟล์รูปภาพ ขนาดไม่เกิน 5MB</p>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </label>
              </div>
            ) : (
              <div className="relative">
                <img
                  src={slipPreview}
                  alt="Payment Slip Preview"
                  className="w-full max-h-80 object-contain rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800"
                />
                <button
                  type="button"
                  onClick={handleRemoveSlip}
                  className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-2 shadow-lg transition"
                  title="ลบรูปภาพ"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-white rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition font-semibold"
            >
              ยกเลิก
            </button>
            <button
              type="button"
              onClick={handleUploadSlip}
              disabled={!slipFile || countdownMs === 0}
              className="px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg transition font-semibold shadow-lg flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              อัพโหลดสลิป
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportCreditMemberBet;
